import React, { useState, useEffect, useMemo } from 'react';
import { Server, User, Key, ArrowLeft, Save, Info, AlertCircle, CheckCircle, Hash, Store, Clock, Plus, Trash2 } from 'lucide-react';

const emptyRestaurant = () => ({ restaurantName: '', toastRestaurantId: '' });

const ToastSftpConfig = ({ selectedLocation, onSave, onBack, isLoading, error, initialConfig = null }) => {
  // SFTP connection is shared across every restaurant in this location's config
  const [connection, setConnection] = useState({
    sftpUrl: '',
    sftpUser: '',
    privateKey: '',
    includePayroll: false,
  });
  // One or more Toast restaurants, each with its own name + Toast Restaurant ID
  const [restaurants, setRestaurants] = useState([emptyRestaurant()]);
  const [touchedConn, setTouchedConn] = useState({});
  const [touchedRest, setTouchedRest] = useState([{}]);
  const [submitted, setSubmitted] = useState(false);

  // Load existing config. Migrates a legacy single-restaurant config into the
  // restaurants[] list so adding another never drops the original.
  useEffect(() => {
    const cfg = initialConfig?.sftpConfig;
    if (!cfg) return;
    setConnection({
      sftpUrl: cfg.sftpUrl || '',
      sftpUser: cfg.sftpUser || '',
      privateKey: cfg.privateKey || '',
      includePayroll: !!cfg.includePayroll,
    });
    if (Array.isArray(cfg.restaurants) && cfg.restaurants.length > 0) {
      // Keep the full objects (incl. tracking) so they round-trip on save
      const loaded = cfg.restaurants.map(r => ({
        ...r,
        restaurantName: r.restaurantName || '',
        toastRestaurantId: (r.toastRestaurantId ?? '').toString(),
      }));
      setRestaurants(loaded);
      setTouchedRest(loaded.map(() => ({})));
    } else if (cfg.restaurantName || cfg.toastRestaurantId) {
      setRestaurants([{
        restaurantName: cfg.restaurantName || '',
        toastRestaurantId: (cfg.toastRestaurantId ?? '').toString(),
      }]);
      setTouchedRest([{}]);
    }
  }, [initialConfig]);

  // ── Validation (derived) ──────────────────────────────────────────────────
  const { connErrors, restErrors, isFormValid } = useMemo(() => {
    const cErr = {};
    if (!connection.sftpUrl.trim()) cErr.sftpUrl = 'SFTP URL is required';
    else if (!connection.sftpUrl.startsWith('sftp://') && !connection.sftpUrl.includes('.')) {
      cErr.sftpUrl = 'Please enter a valid SFTP URL (e.g., sftp://files.toasttab.com)';
    }
    if (!connection.sftpUser.trim()) cErr.sftpUser = 'SFTP username is required';
    if (!connection.privateKey.trim()) cErr.privateKey = 'Private key is required';
    else if (!connection.privateKey.includes('-----BEGIN') && !connection.privateKey.includes('PRIVATE KEY')) {
      cErr.privateKey = 'Please enter a valid SSH private key';
    }

    const idCounts = {};
    restaurants.forEach(r => {
      const id = (r.toastRestaurantId || '').trim();
      if (id) idCounts[id] = (idCounts[id] || 0) + 1;
    });
    const rErr = restaurants.map(r => {
      const e = {};
      if (!r.restaurantName.trim()) e.restaurantName = 'Restaurant name is required';
      const id = (r.toastRestaurantId || '').trim();
      if (!id) e.toastRestaurantId = 'Toast Restaurant ID is required';
      else if (idCounts[id] > 1) e.toastRestaurantId = 'Duplicate Toast Restaurant ID';
      return e;
    });

    const valid =
      Object.keys(cErr).length === 0 &&
      restaurants.length > 0 &&
      rErr.every(e => Object.keys(e).length === 0);
    return { connErrors: cErr, restErrors: rErr, isFormValid: valid };
  }, [connection, restaurants]);

  // ── Field updaters ────────────────────────────────────────────────────────
  const updateConnection = (field, value) => setConnection(prev => ({ ...prev, [field]: value }));
  const blurConnection = (field) => setTouchedConn(prev => ({ ...prev, [field]: true }));

  const updateRestaurant = (index, field, value) => {
    setRestaurants(prev => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };
  const blurRestaurant = (index, field) => {
    setTouchedRest(prev => prev.map((t, i) => (i === index ? { ...t, [field]: true } : t)));
  };
  const addRestaurant = () => {
    setRestaurants(prev => [...prev, emptyRestaurant()]);
    setTouchedRest(prev => [...prev, {}]);
  };
  const removeRestaurant = (index) => {
    if (restaurants.length <= 1) return;
    setRestaurants(prev => prev.filter((_, i) => i !== index));
    setTouchedRest(prev => prev.filter((_, i) => i !== index));
  };

  const showConnErr = (field) => (submitted || touchedConn[field]) && connErrors[field];
  const showRestErr = (index, field) => (submitted || touchedRest[index]?.[field]) && restErrors[index]?.[field];

  const handleSave = () => {
    setSubmitted(true);
    if (!isFormValid) return;
    const cleanedRestaurants = restaurants.map(r => ({
      ...r,
      restaurantName: r.restaurantName.trim(),
      toastRestaurantId: r.toastRestaurantId.trim(),
    }));
    onSave({
      ...connection,
      restaurants: cleanedRestaurants,
      // Keep legacy single fields populated (first restaurant) for backward compatibility
      restaurantName: cleanedRestaurants[0].restaurantName,
      toastRestaurantId: cleanedRestaurants[0].toastRestaurantId,
    });
  };

  const inputBase = 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
  const errBorder = (hasErr) => (hasErr ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600');

  return (
    <div className="ep-config-form">
      {/* Header */}
      <div className="flex items-center space-x-2">
        <Server className="w-5 h-5 text-blue-600" />
        <h2 className="ep-config-title">
          Toast SFTP Integration
        </h2>
      </div>

      {/* Location Info */}
      {selectedLocation && (
        <div className="ep-panel">
          <p className="cp-help">
            Configuring SFTP integration for: <strong>{selectedLocation.name}</strong> ({selectedLocation.code})
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="ep-banner ep-tone-info">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="ep-banner-body">
            <p className="font-medium mb-1">Toast SFTP Setup Instructions</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Generate an SSH key pair if you haven't already</li>
              <li>Provide your public key to Toast for authorization</li>
              <li>Add one row per Toast restaurant that shares this SFTP connection</li>
              <li>Paste your private key for authentication</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="ep-banner ep-tone-error">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <p className="ep-banner-body">{error}</p>
          </div>
        </div>
      )}

      {/* Restaurants */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
            <Store className="inline-block w-4 h-4 mr-1.5" />
            Restaurants
            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              ({restaurants.length} configured)
            </span>
          </h3>
        </div>

        {restaurants.map((restaurant, index) => (
          <div
            key={index}
            className="p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50/50 dark:bg-gray-800/40 space-y-4"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Restaurant {index + 1}
              </span>
              <button
                type="button"
                onClick={() => removeRestaurant(index)}
                disabled={restaurants.length <= 1}
                title={restaurants.length <= 1 ? 'At least one restaurant is required' : 'Remove this restaurant'}
                className="flex items-center text-xs text-red-600 dark:text-red-400 hover:text-red-700
                           disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 size={14} className="mr-1" />
                Remove
              </button>
            </div>

            <div className="ep-grid">
              {/* Restaurant Name */}
              <div>
                <label className="cp-label">
                  <Store className="inline-block w-4 h-4 mr-1" />
                  Restaurant Name <span className="ep-req">*</span>
                </label>
                <input
                  type="text"
                  value={restaurant.restaurantName}
                  onChange={(e) => updateRestaurant(index, 'restaurantName', e.target.value)}
                  onBlur={() => blurRestaurant(index, 'restaurantName')}
                  placeholder="e.g., Coho Cafe"
                  className={`${inputBase} ${errBorder(showRestErr(index, 'restaurantName'))}`}
                />
                {showRestErr(index, 'restaurantName') && (
                  <p className="cp-help cp-error">{restErrors[index].restaurantName}</p>
                )}
              </div>

              {/* Toast Restaurant ID */}
              <div>
                <label className="cp-label">
                  <Hash className="inline-block w-4 h-4 mr-1" />
                  Toast Restaurant ID <span className="ep-req">*</span>
                </label>
                <input
                  type="text"
                  value={restaurant.toastRestaurantId}
                  onChange={(e) => updateRestaurant(index, 'toastRestaurantId', e.target.value)}
                  onBlur={() => blurRestaurant(index, 'toastRestaurantId')}
                  placeholder="e.g., abc123-def456-ghi789"
                  className={`${inputBase} ${errBorder(showRestErr(index, 'toastRestaurantId'))}`}
                />
                {showRestErr(index, 'toastRestaurantId') && (
                  <p className="cp-help cp-error">{restErrors[index].toastRestaurantId}</p>
                )}
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addRestaurant}
          className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400
                     border border-dashed border-blue-300 dark:border-blue-700 rounded-md
                     hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <Plus size={16} className="mr-1.5" />
          Add Restaurant
        </button>
      </div>

      {/* SFTP Connection (shared) */}
      <div className="space-y-5 pt-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
          <Server className="inline-block w-4 h-4 mr-1.5" />
          SFTP Connection
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            (shared by all restaurants above)
          </span>
        </h3>

        {/* SFTP URL */}
        <div>
          <label className="cp-label">
            <Server className="inline-block w-4 h-4 mr-1" />
            SFTP URL <span className="ep-req">*</span>
          </label>
          <input
            type="text"
            value={connection.sftpUrl}
            onChange={(e) => updateConnection('sftpUrl', e.target.value)}
            onBlur={() => blurConnection('sftpUrl')}
            placeholder="sftp://files.toasttab.com"
            className={`${inputBase} ${errBorder(showConnErr('sftpUrl'))}`}
          />
          {showConnErr('sftpUrl') && (
            <p className="cp-help cp-error">{connErrors.sftpUrl}</p>
          )}
          <p className="cp-help">
            The SFTP server URL provided by Toast
          </p>
        </div>

        {/* SFTP User */}
        <div>
          <label className="cp-label">
            <User className="inline-block w-4 h-4 mr-1" />
            SFTP Username <span className="ep-req">*</span>
          </label>
          <input
            type="text"
            value={connection.sftpUser}
            onChange={(e) => updateConnection('sftpUser', e.target.value)}
            onBlur={() => blurConnection('sftpUser')}
            placeholder="your-toast-username"
            className={`${inputBase} ${errBorder(showConnErr('sftpUser'))}`}
          />
          {showConnErr('sftpUser') && (
            <p className="cp-help cp-error">{connErrors.sftpUser}</p>
          )}
          <p className="cp-help">
            Your Toast SFTP account username
          </p>
        </div>

        {/* Private Key */}
        <div>
          <label className="cp-label">
            <Key className="inline-block w-4 h-4 mr-1" />
            Private Key <span className="ep-req">*</span>
          </label>
          <textarea
            value={connection.privateKey}
            onChange={(e) => updateConnection('privateKey', e.target.value)}
            onBlur={() => blurConnection('privateKey')}
            placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;...&#10;-----END OPENSSH PRIVATE KEY-----"
            rows={6}
            className={`${inputBase} font-mono text-sm ${errBorder(showConnErr('privateKey'))}`}
          />
          {showConnErr('privateKey') && (
            <p className="cp-help cp-error">{connErrors.privateKey}</p>
          )}
          <p className="cp-help">
            Your SSH private key for SFTP authentication (stored securely)
          </p>
        </div>

        {/* Include Payroll Checkbox */}
        <div className="ep-config-divider">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={connection.includePayroll}
              onChange={(e) => updateConnection('includePayroll', e.target.checked)}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <div>
              <span className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <Clock className="inline-block w-4 h-4 mr-1" />
                Include Timekeeping/Payroll Integration
              </span>
              <p className="cp-help">
                Also import TimeEntries.csv for employee time tracking and payroll cost data
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Validation Summary */}
      {isFormValid && (
        <div className="ep-banner ep-tone-ok">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <p className="ep-banner-body">
              Configuration is valid and ready to save
            </p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="ep-config-foot">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600
                   text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700
                   focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <ArrowLeft className="mr-2" size={16} />
          Back to Setup
        </button>

        <button
          onClick={handleSave}
          disabled={isLoading || !isFormValid}
          className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                   disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2" size={16} />
              Save Configuration
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ToastSftpConfig;
